import { useEffect, useMemo, useState } from 'react';
import { previewTemplate } from '../api/templates';
import { TemplatePreviewRequest } from '../types';

interface Props {
  contextRequest: Omit<TemplatePreviewRequest, 'template' | 'asJson'>;
  contextHint?: string;
  initialTemplate?: string;
}

export function TemplatePlayground({ contextRequest, contextHint, initialTemplate = '' }: Props) {
  const [template, setTemplate] = useState(initialTemplate);
  const [resolved, setResolved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const stableContext = useMemo(() => JSON.stringify(contextRequest), [contextRequest]);

  useEffect(() => {
    if (!template.trim()) {
      setResolved(null);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setPending(true);
    const handle = setTimeout(() => {
      previewTemplate({ ...JSON.parse(stableContext), template, asJson: false })
        .then(r => {
          if (ctrl.signal.aborted) return;
          setResolved(r.resolved);
          setError(r.error);
        })
        .catch(err => {
          if (ctrl.signal.aborted) return;
          setError(err?.message ?? String(err));
          setResolved(null);
        })
        .finally(() => { if (!ctrl.signal.aborted) setPending(false); });
    }, 300);
    return () => {
      ctrl.abort();
      clearTimeout(handle);
    };
  }, [template, stableContext]);

  return (
    <div className="space-y-2">
      {contextHint && (
        <p className="text-xs text-muted-foreground">{contextHint}</p>
      )}
      <textarea
        className="w-full border border-input rounded p-2 text-xs font-mono bg-background min-h-[80px]"
        placeholder="{{body.field}} or math: $body.count * 2"
        value={template}
        onChange={e => setTemplate(e.target.value)}
      />
      <div className="text-xs">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-muted-foreground">Resolved:</span>
          {pending && <span className="text-muted-foreground">…</span>}
        </div>
        {error ? (
          <pre className="p-2 bg-destructive/10 text-destructive rounded-md font-mono whitespace-pre-wrap break-all">
            {error}
          </pre>
        ) : resolved !== null ? (
          <pre className="p-2 bg-muted text-success rounded-md font-mono whitespace-pre-wrap break-all">
            {resolved}
          </pre>
        ) : (
          <p className="text-muted-foreground">Type a template to see its resolved value.</p>
        )}
      </div>
    </div>
  );
}
